import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as GoHomeIcon } from '@src/assets/icons/go-home.svg';
import { ArcButton } from '@src/core/ui';
import { ArcSocketContext } from '@src/core/socket';
import { GoHomeActionProps } from './types';
import { EnRouteCommandEnum, EnRouteEventTypeEnum } from '@src/features/order/pages/take-off-to-en-route-page/entities';
import { useConfirm } from '@src/core/controllers/confirm-controller/hooks';
import { CommandStatusEnum } from '@src/features/order/entities';
import { useCommandPending } from '@src/features/order/hooks';
import { clearActiveGoHere } from '@src/features/drone/utils/go-here-registry';

const ACCEPT_TIMEOUT_MS = 15000;

const GoHomeAction: React.FC<GoHomeActionProps> = (props) => {
  const { serialNumber, resetGoHere, sx } = props;
  const { t } = useTranslation();
  const { arcSocket } = React.useContext(ArcSocketContext);
  const { confirm } = useConfirm();

  const { isPending, start } = useCommandPending({
    command: CommandStatusEnum.DELIVERY_COMPLETED,
    serialNumber,
    acceptTimeoutMs: ACCEPT_TIMEOUT_MS,
    timeoutMessage: t('order.goHome.timeout'),
  });

  const goHome = async () => {
    const confirmed = await confirm(
      'The drone will fly to the home point via return route.',
      'Confirm Go Home?'
    );
    if (!confirmed) return;

    const socket = arcSocket();
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      toast.error(t('order.goHome.noConnection'));
      return;
    }

    socket.send(
      JSON.stringify({
        type: EnRouteEventTypeEnum.COMMAND,
        command: EnRouteCommandEnum.DELIVERY_COMPLETED,
        serialNumber,
      })
    );

    resetGoHere();
    clearActiveGoHere(serialNumber);
    start();
  };

  return (
    <ArcButton.brand
      sx={{ width: 200, '& .MuiTypography-root': { color: 'background.paper' }, ...sx }}
      startIcon={<GoHomeIcon width={22} height={22} />}
      isLoading={isPending}
      onClick={goHome}>
      GO HOME
    </ArcButton.brand>
  );
};

export default GoHomeAction;
